/**
 * Colour swatch behaviour for snippets/citron-card.liquid.
 *
 * Everything else the card does -- opening the product page from a click
 * anywhere on it, modifier-clicks, the view transition -- belongs to Horizon's
 * <product-card> element. This file only handles the swatches, which that
 * element deliberately ignores because they are buttons.
 *
 * Delegated from the document, so it covers cards added later by infinite
 * scroll, a filter change or a section re-render without rebinding anything.
 *
 * Hover previews a colour; clicking commits to it and repoints the card's link
 * at that variant, so the product page opens on the colour the shopper chose.
 */

(() => {
  const CARD = '[data-ctr-pcard]';
  const SWATCH = '.ctr-sw[data-ctr-sw-src]';

  /** Remembers each card's own image so a cancelled preview can be undone. */
  const original = new WeakMap();

  /**
   * @param {Element} swatch
   * @returns {{card: Element, img: HTMLImageElement} | null}
   */
  const parts = (swatch) => {
    const card = swatch.closest(CARD);
    const img = card?.querySelector('img.ctr-pcard__img');
    return card && img instanceof HTMLImageElement ? { card, img } : null;
  };

  /** @param {HTMLImageElement} img */
  const remember = (img) => {
    if (!original.has(img)) original.set(img, { src: img.src, srcset: img.srcset });
  };

  /**
   * @param {HTMLImageElement} img
   * @param {string} src
   * @param {string} srcset
   */
  const paint = (img, src, srcset) => {
    if (!src || img.src === src) return;
    img.src = src;
    img.srcset = srcset || '';
  };

  /** @param {Element} swatch */
  const preview = (swatch) => {
    const found = parts(swatch);
    if (!found) return;
    remember(found.img);
    paint(found.img, swatch.getAttribute('data-ctr-sw-src') || '', swatch.getAttribute('data-ctr-sw-srcset') || '');
  };

  /** @param {Element} swatch */
  const restore = (swatch) => {
    const found = parts(swatch);
    if (!found) return;
    // A committed choice is the card's image now, so there is nothing to undo.
    const chosen = found.card.querySelector(`${SWATCH}[aria-pressed="true"]`);
    if (chosen) {
      paint(found.img, chosen.getAttribute('data-ctr-sw-src') || '', chosen.getAttribute('data-ctr-sw-srcset') || '');
      return;
    }
    const was = original.get(found.img);
    if (was) paint(found.img, was.src, was.srcset);
  };

  /** @param {Element} swatch */
  const commit = (swatch) => {
    const found = parts(swatch);
    if (!found) return;

    for (const other of found.card.querySelectorAll(SWATCH)) {
      other.setAttribute('aria-pressed', String(other === swatch));
    }
    preview(swatch);

    // The card now shows a colour the shopper picked, so the hover crossfade
    // to the product's second photo has to stand down -- otherwise moving the
    // pointer back over the card covers that colour with another image.
    found.card.classList.add('ctr-pcard--swapped');

    const link = found.card.querySelector('a.ctr-pcard__link');
    const id = swatch.getAttribute('data-variant-id');
    if (link instanceof HTMLAnchorElement && id) {
      const url = new URL(link.href, window.location.origin);
      url.searchParams.set('variant', id);
      link.href = url.toString();
    }
  };

  /**
   * @param {Event} event
   * @param {(swatch: Element) => void} action
   */
  const on = (event, action) => {
    const target = event.target;
    if (!(target instanceof Element)) return;
    const swatch = target.closest(SWATCH);
    if (swatch) action(swatch);
  };

  // pointerover/out rather than enter/leave: those two do not bubble, so they
  // cannot be delegated from the document.
  document.addEventListener('pointerover', (event) => {
    if (event.pointerType !== 'mouse') return;
    on(event, preview);
  });

  document.addEventListener('pointerout', (event) => {
    if (event.pointerType !== 'mouse') return;
    on(event, restore);
  });

  document.addEventListener('focusin', (event) => on(event, preview));
  document.addEventListener('focusout', (event) => on(event, restore));
  document.addEventListener('click', (event) => on(event, commit));
})();
